import React from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import { makeMatcher } from '../utils/search';
import { salesAPI } from '../api';

// ─── Выбор контрагента с поиском ──────────────────────────────────────────────
//
// Был обычный выпадающий список: контрагентов много, и нужного приходилось
// искать прокруткой. Здесь то же поле, но с поиском — по названию И по ИНН/БИН
// (в 1С контрагента ищут именно по ИНН, менеджеру привычнее набрать цифры).
// Поиск без учёта регистра и по вхождению, а не с начала строки: «замин»
// находит «ООО Zamin» только по началу слова, а по вхождению — всегда.
export default function ClientPicker({ label, clients, value, onChange, placeholder, onCreateNew, searchType }) {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState(null);
  const boxRef = React.useRef(null);
  const listRef = React.useRef(null);

  const selected = clients.find(c => String(c.id) === String(value));

  // Список рисуем поверх страницы, а не внутри формы. Окна отгрузки высокие и
  // прокручиваются внутри себя (maxHeight + overflow), а браузер обрезает по
  // этой рамке всё, что вылезло за неё. Контрагент — первое поле формы, список
  // раскрывался вниз, за край окна, и выбрать в нём было нечего: пусто.
  const place = React.useCallback(() => {
    if (!boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom, bottom: r.top, width: r.width });
  }, []);

  // Клик мимо — закрыть список, иначе он висит поверх формы
  React.useEffect(() => {
    const away = (e) => {
      const inBox  = boxRef.current && boxRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inBox && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Готовый список — это контрагенты СВОЕГО цеха: те, с кем он уже работал.
  // База на весь завод одна, и заказчик, до сих пор возивший только в Шымкент,
  // в списке Алматы не лежит. Менеджер набирает название, не находит и заводит
  // вторую карточку — один и тот же ИИН разъезжается по двум записям.
  //
  // Поэтому, как только набрали пару букв, спрашиваем сервер: там поиск идёт
  // по всей базе, без деления на цеха. Найденное подмешиваем к своим.
  const [remote, setRemote] = React.useState([]);
  const [wholeBook, setWholeBook] = React.useState([]);

  // Открыли список — подтягиваем весь справочник. Свой цех показывает только
  // тех, с кем работал, и в новом цехе это пусто: менеджер, ошибившись в одной
  // букве, не находит ничего и заводит вторую карточку. Пусть лучше листает
  // глазами. Тянем один раз за открытие и только когда список правда открыт.
  React.useEffect(() => {
    if (!open || wholeBook.length) return;
    salesAPI.getClients(searchType, '', true)
      .then(r => setWholeBook(Array.isArray(r.data) ? r.data : (r.data.results || [])))
      .catch(() => {});
  }, [open, wholeBook.length, searchType]);

  React.useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) { setRemote([]); return; }
    // Пауза, чтобы не слать запрос на каждую букву.
    const timer = setTimeout(() => {
      salesAPI.getClients(searchType, needle)
        .then(r => setRemote(Array.isArray(r.data) ? r.data : (r.data.results || [])))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [q, searchType]);

  const needle = q.trim().toLowerCase();
  const pool = React.useMemo(() => {
    const extra = wholeBook.concat(remote);
    if (!extra.length) return clients;
    const seen = new Set(clients.map(c => String(c.id)));
    const rest = [];
    extra.forEach(c => {
      if (seen.has(String(c.id))) return;
      seen.add(String(c.id));
      rest.push(c);
    });
    // Свои — сверху: с ними этот цех работает каждый день.
    return clients.concat(rest);
  }, [clients, remote, wholeBook]);

  const found = !needle ? pool : pool.filter(c =>
    makeMatcher(needle)(c.name, c.inn_bin, c.phone, c.contact_person)
  );

  const pick = (c) => { onChange(String(c.id)); setQ(''); setOpen(false); };

  const fieldStyle = {
    width: '100%', padding: '9px 12px', background: 'var(--input)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {label && <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{label}</label>}

      {selected && !open ? (
        <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={() => { setOpen(true); setQ(''); }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected.name}
            {selected.inn_bin && <span style={{ color: 'var(--dim)', marginLeft: 8, fontSize: 12 }}>ИНН {selected.inn_bin}</span>}
          </span>
          <button type="button" title={t('Очистить')}
            onClick={e => { e.stopPropagation(); onChange(''); setQ(''); setOpen(true); }}
            style={{ background: 'none', border: 'none', color: 'var(--dim)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      ) : (
        <input
          value={q}
          autoComplete="off"
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'Поиск по названию или ИНН…'}
          style={fieldStyle}
        />
      )}

      {open && rect && createPortal(
        <div ref={listRef} style={{
          position: 'fixed', zIndex: 9000, left: rect.left, width: rect.width,
          ...(window.innerHeight - rect.top < 276
              ? { bottom: window.innerHeight - rect.bottom + 4 }
              : { top: rect.top + 4 }),
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8,
          maxHeight: 260, overflowY: 'auto', boxShadow: '0 12px 30px rgba(0,0,0,.28)',
        }}>
          {found.length === 0 ? (
            <div style={{ padding: '10px 12px', color: 'var(--dim)', fontSize: 13 }}>
              Ничего не найдено
            </div>
          ) : found.slice(0, 60).map(c => (
            <div key={c.id} onClick={() => pick(c)}
              onMouseDown={e => e.preventDefault()}
              style={{
                padding: '9px 12px', cursor: 'pointer', fontSize: 14, color: 'var(--text)',
                display: 'flex', justifyContent: 'space-between', gap: 10,
                background: String(c.id) === String(value) ? 'var(--accent-weak)' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--input)'}
              onMouseLeave={e => e.currentTarget.style.background = String(c.id) === String(value) ? 'var(--accent-weak)' : 'transparent'}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              <span style={{ color: 'var(--dim)', fontSize: 12, flexShrink: 0 }}>
                {c.inn_bin || '—'}
              </span>
            </div>
          ))}
          {found.length > 60 && (
            <div style={{ padding: '8px 12px', color: 'var(--dim)', fontSize: 12 }}>
              Показаны первые 60 — уточните поиск
            </div>
          )}
          {/* Завести нового предлагаем ПОСЛЕ поиска, как в выборе товара.
              Раньше поля нового контрагента висели под полем всегда — форма
              выглядела так, будто заводить нового и есть обычный путь, и
              менеджер плодил дубли вместо того, чтобы поискать. */}
          {onCreateNew && (
            <div
              onClick={() => { onCreateNew(q.trim()); setOpen(false); }}
              onMouseDown={e => e.preventDefault()}
              style={{
                padding: '10px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                color: 'var(--accent)', borderTop: '1px solid var(--border)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--input)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              ➕ {q.trim() ? `Завести «${q.trim()}»` : 'Завести нового контрагента'}
            </div>
          )}
        </div>,
        document.body)}
    </div>
  );
}
